/**
 * LO QUE LA TARJETA DE VOZ RELLENA POR SU CUENTA, sin React (anexo B §3.2).
 *
 * La tarjeta de confirmación (VoiceTaskPanel + VoiceTaskConfirmation) pone cinco
 * cosas que nadie dijo:
 *
 *   · la HORA ACTUAL si la IA no dio hora (47 tarjetas de la auditoría; W1 la
 *     cambiaba a mano a las 08:00);
 *   · el TRABAJADOR, si los sectores dictados tienen UN solo asignado activo en
 *     `block_worker_assignments` (en Nijasol no salta nunca: cada sector tiene
 *     entre 7 y 11);
 *   · la NAVE del primer sector, cuando se dictaron sectores sin nave;
 *   · la FINCA de esa nave, en ese mismo paso;
 *   · TODOS los sectores de la nave, cuando hay nave y ningún sector.
 *
 * Antes vivían dentro de los dos componentes. Aquí son funciones puras por dos
 * razones: el corpus de voz mide «lo que ve el usuario», que es la salida del
 * motor MÁS estos defaults (y tiene que ser la MISMA regla, no una copia), y
 * final_result necesita saber qué rellenó la tarjeta para medir si se acepta
 * (`card_defaults`). El comportamiento no cambia: el test
 * src/__tests__/voz/voiceCardDefaults.test.ts compara con el código de antes.
 *
 * `card_defaults` guarda campo → valor puesto. La tarjeta no lo pinta (su diseño
 * no cambia); va a final_result como «defaults» con origen «tarjeta».
 */

export type CardDefaultKey = "time" | "assigned_to" | "greenhouse_id" | "location_id" | "block_ids";

/** Lo mínimo de una tarjeta que miran estos defaults (VoiceTaskData lo cumple). */
export interface CardDefaultsTask {
  scheduled_time?: string;
  assigned_to?: string | null;
  worker_name?: string | null;
  location_id?: string | null;
  location_name?: string | null;
  greenhouse_id?: string | null;
  greenhouse_name?: string | null;
  block_ids?: string[];
  block_names?: string[];
  card_defaults?: Record<string, unknown>;
}

export interface BlockOption { id: string; name: string; greenhouse_id: string }
export interface GreenhouseOption { id: string; name: string; location_id: string | null }
export interface LocationOption { id: string; name: string }

/** Anota qué rellenó la tarjeta y con qué valor (sin pisar lo anotado antes). */
function withCardDefault<T extends CardDefaultsTask>(task: T, key: CardDefaultKey, value: unknown): T {
  return { ...task, card_defaults: { ...(task.card_defaults ?? {}), [key]: value } };
}

/* ── Panel (VoiceTaskPanel.processAudio) ─────────────────────────────────── */

/** «HH:mm» de la hora LOCAL del navegador, como hacía el panel. */
export function currentTimeHHmm(now: Date): string {
  return `${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`;
}

/**
 * Hora por defecto = ahora. Con `||`, igual que antes: una hora vacía («») también
 * se rellena. Siempre devuelve un objeto nuevo (el panel hacía `map` con spread).
 */
export function withDefaultTime<T extends CardDefaultsTask>(task: T, now: Date): T {
  if (task.scheduled_time) return { ...task };
  const time = currentTimeHHmm(now);
  return withCardDefault({ ...task, scheduled_time: time }, "time", time);
}

/**
 * ¿Hay que preguntar por el asignado único? Solo sin trabajador y con sectores
 * DICTADOS: el panel lo hace antes de que la tarjeta rellene «todos los sectores»,
 * así que nunca se autoasigna a partir de sectores que puso la propia tarjeta.
 */
export function needsAssigneeLookup(task: CardDefaultsTask): boolean {
  return !task.assigned_to && !!task.block_ids && task.block_ids.length > 0;
}

/** El único usuario distinto entre las asignaciones activas, o null. */
export function uniqueAssignee(assignments: ReadonlyArray<{ user_id: string }> | null | undefined): string | null {
  const ids = [...new Set((assignments || []).map((a) => a.user_id))];
  return ids.length === 1 ? ids[0] : null;
}

/** Pone el asignado único. `fullName` vacío → `worker_name` undefined, como antes. */
export function withAssignee<T extends CardDefaultsTask>(task: T, userId: string, fullName: string | null | undefined): T {
  return withCardDefault({ ...task, assigned_to: userId, worker_name: fullName || undefined }, "assigned_to", userId);
}

/* ── Tarjeta (VoiceTaskConfirmation, efecto nave ↔ sectores) ──────────────── */

export interface StructureOptions {
  /** Los sectores que la tarjeta ofrece (ya sin los excluidos del alcance). */
  blockOptions: BlockOption[];
  /** Las naves que la tarjeta ofrece (ya recortadas por finca). */
  greenhouseOptions: GreenhouseOption[];
  locationOptions: LocationOption[];
}

export interface StructureDefaultResult<T> {
  task: T;
  applied: CardDefaultKey[];
  /** Nave cuyos sectores ya se rellenaron (el `useRef` del componente). */
  autofilledGreenhouseId: string | null;
}

/**
 * Un paso del efecto nave ↔ sectores de la tarjeta. null = no hay nada que hacer.
 *
 *  · sectores sin nave → la nave del PRIMER sector (y su finca si no había);
 *  · nave sin sectores → todos sus sectores, UNA vez por nave: si el usuario
 *    vacía la lista con «Todas las secciones», no se vuelve a rellenar en el mismo
 *    render (02-09-2026: parecía un botón muerto).
 *
 * Idéntico al efecto de antes, incluido que el primer caso no toca
 * `autofilledGreenhouseId` y que un sector que la tarjeta no ofrece no hace nada.
 */
export function cardStructureDefault<T extends CardDefaultsTask>(
  task: T,
  opts: StructureOptions,
  autofilledGreenhouseId: string | null,
): StructureDefaultResult<T> | null {
  const blockIds = task.block_ids || [];
  if (!task.greenhouse_id && blockIds.length > 0) {
    const firstBlock = opts.blockOptions.find((b) => b.id === blockIds[0]);
    if (!firstBlock) return null;
    const gh = opts.greenhouseOptions.find((g) => g.id === firstBlock.greenhouse_id);
    const location_id = task.location_id || gh?.location_id || null;
    let next: T = {
      ...task,
      greenhouse_id: firstBlock.greenhouse_id,
      greenhouse_name: gh?.name || null,
      location_id,
      location_name:
        task.location_name ||
        (gh?.location_id ? (opts.locationOptions.find((l) => l.id === gh.location_id)?.name || null) : null),
    };
    const applied: CardDefaultKey[] = ["greenhouse_id"];
    next = withCardDefault(next, "greenhouse_id", firstBlock.greenhouse_id);
    if (!task.location_id && location_id) {
      applied.push("location_id");
      next = withCardDefault(next, "location_id", location_id);
    }
    return { task: next, applied, autofilledGreenhouseId };
  }
  if (!task.greenhouse_id || blockIds.length > 0) return null;
  if (autofilledGreenhouseId === task.greenhouse_id) return null;
  const ghBlocks = opts.blockOptions.filter((b) => b.greenhouse_id === task.greenhouse_id);
  if (ghBlocks.length === 0) return null;
  const ids = ghBlocks.map((b) => b.id);
  const next = withCardDefault({ ...task, block_ids: ids, block_names: ghBlocks.map((b) => b.name) }, "block_ids", ids);
  return { task: next, applied: ["block_ids"], autofilledGreenhouseId: task.greenhouse_id };
}

/* ── Todo junto, en el orden de la tarjeta (corpus) ──────────────────────── */

export interface CardDefaultsContext extends StructureOptions {
  now: Date;
  /** Asignaciones activas de la empresa (block_worker_assignments, is_active). */
  assignments?: ReadonlyArray<{ user_id: string; block_id: string }>;
  /** Nombre por user_id, para `worker_name`. */
  workerNames?: Record<string, string | null | undefined>;
}

/**
 * Lo que vería el usuario al abrir la tarjeta: hora → (enriquecido de productos,
 * que no toca estos campos) → asignado único con los sectores DICTADOS → nave ↔
 * sectores hasta que ya no cambie nada (lo que hacen los re-renders de React).
 */
export function applyCardDefaults<T extends CardDefaultsTask>(task: T, ctx: CardDefaultsContext): T {
  let t = withDefaultTime(task, ctx.now);
  if (needsAssigneeLookup(t)) {
    const blocks = new Set(t.block_ids);
    const uid = uniqueAssignee((ctx.assignments || []).filter((a) => blocks.has(a.block_id)));
    if (uid) t = withAssignee(t, uid, ctx.workerNames?.[uid] ?? null);
  }
  let autofilled: string | null = null;
  for (let i = 0; i < 4; i++) {
    const r = cardStructureDefault(t, ctx, autofilled);
    if (!r) break;
    t = r.task;
    autofilled = r.autofilledGreenhouseId;
  }
  return t;
}
